import type { ReportTheme, ThemeBlock, FontFamily } from '../../types/reportTheme'

export const FONT_STACK: Record<FontFamily, string> = {
  sans: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  serif: 'ui-serif, Georgia, Cambria, "Times New Roman", serif',
  mono: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
}

export function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v : undefined
}

export function num(v: unknown, fallback: number): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return fallback
}

/** Convertit un hex (#rgb ou #rrggbb) en rgba() ; repli sombre si invalide. */
export function hexToRgba(hex: unknown, alpha: number): string {
  const m = typeof hex === 'string' ? /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(hex) : null
  if (!m) return `rgba(12,15,26,${alpha})`
  let h = m[1]
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

type LogoSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const LOGO_IMG: Record<LogoSize, string> = {
  xs: 'h-5 max-w-[80px]',
  sm: 'h-7 max-w-[120px]',
  md: 'h-10 max-w-[160px]',
  lg: 'h-16 max-w-[300px]',
  xl: 'h-24 max-w-[380px]',
}
const LOGO_BOX: Record<LogoSize, string> = {
  xs: 'h-5 w-12 text-[7px]',
  sm: 'h-7 w-16 text-[8px]',
  md: 'h-10 w-24 text-[9px]',
  lg: 'h-16 w-40 text-sm',
  xl: 'h-24 w-56 text-base',
}

export function LogoSlot({
  url, alt, label, onDark, size = 'md', shadow,
}: { url: string | null; alt: string; label: string; onDark?: boolean; size?: LogoSize; shadow?: boolean }) {
  if (url) {
    return (
      <img
        src={url}
        alt={alt}
        className={`${LOGO_IMG[size]} w-auto object-contain`}
        style={shadow ? { filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.5))' } : undefined}
      />
    )
  }
  return (
    <span
      className={`flex ${LOGO_BOX[size]} items-center justify-center rounded border border-dashed font-medium uppercase tracking-wider ${
        onDark ? 'border-white/40 text-white/70' : 'border-[#C8CBD8] text-[#8B8FA7]'
      }`}
    >
      {label}
    </span>
  )
}

/** Dimensions figées de la page de garde (mêmes unités que le canvas rapport = 1 page A4). */
export const COVER_W = 1100
export const COVER_H = 1556

interface ThemeCoverProps {
  theme: ReportTheme
  /** Titre affiché (défaut : cover.title ?? nom du thème). */
  title?: string
  /** Ligne client (ex : « Ferme des Moines · fermedesmoines.fr »). */
  clientLabel: string
  /** Période affichée (ex : « 1 – 31 juillet 2026 »). */
  periodLabel: string
}

/**
 * Surface de page de garde brandée par le thème (couleurs/logos/cartouche/bandeau/image de fond).
 * Dimensions FIGÉES 1100×1556 (= 1 page A4) : les éléments sont dimensionnés pour cette échelle.
 * Les consommateurs affichent la couverture soit à 1:1 (PDF/snapshot), soit réduite (aperçu /themes).
 */
export function ThemeCover({ theme, title, clientLabel, periodLabel }: ThemeCoverProps) {
  const cover = theme.cover as ThemeBlock
  const contact = theme.contact as ThemeBlock
  const primary = theme.primaryColor
  const secondary = theme.secondaryColor

  const coverTitle = title ?? str(cover.title) ?? theme.name ?? 'Rapport de performance'
  const coverBg = str(cover.backgroundImageUrl)

  const cartouche = !!cover.cartouche
  const radius = num(cover.radius, 16)
  const cartoucheBg = hexToRgba(cover.cartoucheColor ?? '#0C0F1A', num(cover.cartoucheOpacity, 45) / 100)
  const footerBand = !!cover.footerBand
  const plateStyle = cartouche ? { backgroundColor: cartoucheBg, borderRadius: radius } : undefined
  const platePad = cartouche ? 'px-6 py-5' : ''
  const textShadow = coverBg && !cartouche ? { textShadow: '0 2px 14px rgba(0,0,0,0.6)' } : undefined

  return (
    <div
      data-testid="preview-cover"
      className="relative flex flex-col justify-between overflow-hidden p-16 text-white"
      style={{
        width: COVER_W,
        height: COVER_H,
        backgroundColor: primary,
        ...(coverBg ? { backgroundImage: `url("${coverBg}")`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}),
      }}
    >
      {coverBg ? <div className="absolute inset-0" style={{ backgroundColor: 'rgba(12,15,26,0.42)' }} /> : null}
      <div className="absolute inset-x-0 top-0 z-10 h-3" style={{ backgroundColor: secondary }} />

      {/* Haut : logo agence + libellé Rapport */}
      <div className="relative z-10 flex items-start justify-between">
        <span data-testid="cover-plate-agency" className={`inline-flex ${platePad}`} style={plateStyle}>
          <LogoSlot url={theme.agencyLogoUrl} alt="Logo agence" label="AGENCE" onDark size="lg" shadow={!!textShadow} />
        </span>
        <span
          className={`text-xl font-semibold uppercase tracking-[0.2em] ${cartouche ? platePad : 'rounded-full bg-white/15 px-6 py-2.5'}`}
          style={cartouche ? plateStyle : textShadow}
        >
          Rapport
        </span>
      </div>

      {/* Milieu : logo client + titre */}
      <div className="relative z-10 flex flex-col items-start gap-7">
        <LogoSlot url={theme.clientLogoUrl} alt="Logo client" label="LOGO CLIENT" onDark size="xl" shadow={!!textShadow} />
        <div className={`flex flex-col gap-3 ${platePad}`} style={plateStyle} data-testid="cover-plate-title">
          <h2 className="text-6xl font-bold leading-[1.05]" style={textShadow}>{coverTitle}</h2>
          <p className="text-3xl text-white/90" style={textShadow}>{clientLabel}</p>
          <p className="text-2xl text-white/75" style={textShadow}>{periodLabel}</p>
        </div>
      </div>

      {/* Bandeau de pied de couverture optionnel */}
      {footerBand ? (
        <div
          data-testid="cover-footer-band"
          className="absolute inset-x-0 bottom-0 z-0 h-44"
          style={{ backgroundColor: cartoucheBg }}
        />
      ) : null}
      <div className="relative z-10 flex items-end justify-between text-xl text-white/85">
        {contact.enabled ? (
          <div className="leading-snug" style={textShadow}>
            {str(contact.name) ? <p className="text-2xl font-semibold text-white">{str(contact.name)}</p> : <p className="text-2xl font-semibold text-white">Votre contact</p>}
            {str(contact.email) ? <p className="mt-1">{str(contact.email)}</p> : <p className="mt-1">contact@agence.fr</p>}
          </div>
        ) : (
          <span />
        )}
        <span className="text-white/75" style={textShadow}>Préparé par {str(cover.signature) ?? 'votre agence'}</span>
      </div>
    </div>
  )
}
